import { useState, useEffect } from 'react';
import { MdWarningAmber } from 'react-icons/md';
import { getClientDomain, getToken, translate } from '../../../../utils/Common';
import Spinner from '../../../../components/Spinner';
import Table from '../../../../components/data/Table';

/**
 * Partes [yyyy, mm, dd, hh, mm, ss] de una fecha, tal cual vienen, SIN convertir zona horaria:
 * el driver serializa los datetime de SQL como ISO en UTC ('2026-08-21T16:42:36.353Z'), asi que
 * leer el string es lo que devuelve la hora que guarda la base — pasarlo por los getters locales
 * de Date la correria. Acepta ISO y 'yyyy-mm-dd hh:mm:ss' (lo que da el endpoint de excepciones).
 */
const getDateParts = (value) => {
    const text = value instanceof Date ? value.toISOString() : String(value ?? '');
    const match = text.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/);

    return match ? match.slice(1, 7) : null;
};

/** Clave ordenable 'yyyymmddhhmmss': se compara como texto y no depende de zonas horarias. */
const getSortKey = (value) => getDateParts(value)?.join('') ?? null;

/**
 * dd/mm/yyyy HH:mm:ss, en 24h.
 *
 * Se formatea desde la hora real (FechaMov en los estatus, la fecha del endpoint en las
 * excepciones), que es la unica en 24h: la vista VwGetEditTrackingPaquetes devuelve
 * Fecha_Movimiento en 12h y sin AM/PM, asi que '01:33:36' podian ser las 13:33.
 *
 * El orden NO usa esto: para ordenar esta getSortKey, que trabaja sobre esas mismas horas reales.
 */
const formatDate = (value) => {
    const parts = getDateParts(value);
    if (!parts) return value ?? '';

    const [year, month, day, hour, minute, second] = parts;

    return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
};

/**
 * Mezcla los estatus con las excepciones SIN reordenar los estatus: cada excepcion se inserta
 * despues del ultimo estatus cuya fecha sea <= la suya.
 *
 * No se ordena todo por fecha a proposito. ServTracking mezcla dos relojes: las filas que inserta
 * el api usan la hora del servidor (UTC) y las que vienen del locker su hora local, con horas de
 * diferencia entre si. Ordenar por esa columna manda el "paquete creado" al final del timeline. El
 * orden de la vista (IdTracking) si es el orden real de los eventos, asi que se respeta.
 */
const mergeTrackingWithExceptions = (trackingRows, exceptionRows) => {
    const merged = [...trackingRows];

    const pending = [...exceptionRows].sort((a, b) => (a.sortKey && b.sortKey)
        ? a.sortKey.localeCompare(b.sortKey)
        : 0);

    for (const exception of pending) {
        // por defecto al inicio: la excepcion es anterior a todos los estatus fechados
        let position = 0;

        for (let index = merged.length - 1; index >= 0; index--) {
            const row = merged[index];

            if (row.isException || !row.sortKey || !exception.sortKey) continue;

            if (row.sortKey.localeCompare(exception.sortKey) <= 0) {
                position = index + 1;
                break;
            }
        }

        // si el ancla ya tiene excepciones detras, esta va despues (llegan en orden ascendente)
        while (position < merged.length && merged[position].isException) position++;

        merged.splice(position, 0, exception);
    }

    return merged;
};

/**
 * Nombre de la excepcion en el idioma activo. El catalogo (ConfExceptions) guarda un nombre por
 * idioma en columnas distintas, no una clave de traduccion, asi que se elige aca con el mismo
 * criterio que usa `translate` (localStorage 'language', si no el del navegador).
 */
const getExceptionName = (exception) => {
    const lang = localStorage.getItem('language') ?? window.navigator.language?.split('-')[0];

    const byLang = lang === 'es' ? exception?.espName
        : lang === 'pt' ? exception?.porName
        : exception?.engName;

    // si el catalogo no resolvio la excepcion (fue borrada de ConfExceptions) el reporte igual se
    // muestra con su id: mismo criterio del api, que nunca descarta el reporte del repartidor
    return byLang || exception?.description || exception?.code || `#${exception?.exceptionId ?? '?'}`;
};

// tabla con el tracking de boxit
const TableEstatusBoxit = ({ idPaquete, updateComponent }) => {
    // tracking boxit
    const [boxitTrackingPaquete, setBoxitTrackingPaquete] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [statusBoxitTracking, setStatusBoxitTracking] = useState('');
    
const trackingTemplates = {
  PACKAGE_CREATED: { primary: translate("global.statusBoxitTracking.PACKAGE_CREATED") },
  PACKAGE_SIZE_UPDATED: { primary: translate("global.statusBoxitTracking.PACKAGE_SIZE_UPDATED") },
  PLATFORM_CHANGED: {
    primary: translate("global.statusBoxitTracking.PLATFORM_CHANGED"),
    fallback: translate("global.statusBoxitTracking.PLATFORM_CHANGED.secondary")
  },
  EMAIL_UPDATED: {
    primary: translate("global.statusBoxitTracking.EMAIL_UPDATED"),
    fallback: translate("global.statusBoxitTracking.EMAIL_UPDATED.secondary")
  },
  DOOR_RESERVED: {
    primary: translate("global.statusBoxitTracking.DOOR_RESERVED"),
    fallback: translate("global.statusBoxitTracking.DOOR_RESERVED.secondary")
  },
  DOOR_RESERVATION_REMOVED: { primary: translate("global.statusBoxitTracking.DOOR_RESERVATION_REMOVED") },
  DUE_DATE_UPDATED: { primary: translate("global.statusBoxitTracking.DUE_DATE_UPDATED") },
  PACKAGE_DELIVERED: { primary: translate("global.statusBoxitTracking.PACKAGE_DELIVERED") },
  CLIENT_CHANGED: {
    primary: translate("global.statusBoxitTracking.CLIENT_CHANGED"),
    fallback: translate("global.statusBoxitTracking.CLIENT_CHANGED.secondary")
  },
  PACKAGE_CANCELLED: { primary: translate("global.statusBoxitTracking.PACKAGE_CANCELLED") },
  PACKAGE_CREATED_NO_RESERVATION: { primary: translate("global.statusBoxitTracking.PACKAGE_CREATED_NO_RESERVATION") },
  PACKAGE_CREATED_WITH_RESERVATION: { primary: translate("global.statusBoxitTracking.PACKAGE_CREATED_WITH_RESERVATION") },
  PACKAGE_CREATED_FOR_SEND: { primary: translate("global.statusBoxitTracking.PACKAGE_CREATED_FOR_SEND") },
  DOOR_RESERVED_ONLY: {
    primary: translate("global.statusBoxitTracking.DOOR_RESERVED_ONLY"),
    fallback: translate("global.statusBoxitTracking.DOOR_RESERVED_ONLY.secondary")
  },
  PACKAGE_DELIVERED_IN_LOCKER: { primary: translate("global.statusBoxitTracking.PACKAGE_DELIVERED_IN_LOCKER") },
  PACKAGE_PICKED_UP_FROM_LOCKER: { primary: translate("global.statusBoxitTracking.PACKAGE_PICKED_UP_FROM_LOCKER") },
  PACKAGE_PICKED_UP_BY_AGENT_TIMEOUT: { primary: translate("global.statusBoxitTracking.PACKAGE_PICKED_UP_BY_AGENT_TIMEOUT") },
  PACKAGE_UPDATED: { primary: translate("global.statusBoxitTracking.PACKAGE_UPDATED") },
  PACKAGE_RECEIVED_PASSWORD_FROM_GOLOCKER: { primary: translate("global.statusBoxitTracking.PACKAGE_RECEIVED_PASSWORD_FROM_GOLOCKER") },
  PACKAGE_PICKED_UP_BY_ADMIN: { primary: translate("global.statusBoxitTracking.PACKAGE_PICKED_UP_BY_ADMIN") },
  PACKAGE_RETURNED_TO_PREVIOUS_STATUS: { primary: translate("global.statusBoxitTracking.PACKAGE_RETURNED_TO_PREVIOUS_STATUS") },
  PACKAGE_CREATED_DIRECT_DELIVERY: { primary: translate("global.statusBoxitTracking.PACKAGE_CREATED_DIRECT_DELIVERY") },
  PACKAGE_CREATED_DIRECT_RETURN: { primary: translate("global.statusBoxitTracking.PACKAGE_CREATED_DIRECT_RETURN") },
  PACKAGE_PICKED_UP_BY_DRIVER: { primary: translate("global.statusBoxitTracking.PACKAGE_PICKED_UP_BY_DRIVER") },
  PACKAGE_DELIVERED_ON_SITE: { primary: translate("global.statusBoxitTracking.PACKAGE_DELIVERED_ON_SITE") },
  PACKAGE_CREATED_FOR_DELIVERY: { primary: translate("global.statusBoxitTracking.PACKAGE_CREATED_FOR_DELIVERY") },
  PACKAGE_CREATED_FOR_ADMIN_RECOVERY: { primary: translate("global.statusBoxitTracking.PACKAGE_CREATED_FOR_ADMIN_RECOVERY") },
  PACKAGE_CANCELLED_BY_GOLOCKER: { primary: translate("global.statusBoxitTracking.PACKAGE_CANCELLED_BY_GOLOCKER") },
  PACKAGE_RETURNED: { primary: translate("global.statusBoxitTracking.PACKAGE_RETURNED") },
  PACKAGE_ASSIGNED_CREATED: { primary: translate("global.statusBoxitTracking.PACKAGE_ASSIGNED_CREATED") },
  PACKAGE_PICKED_UP: { primary: translate("global.statusBoxitTracking.PACKAGE_PICKED_UP") },
};

const renderTrackingMessage = (idTrackingDescripcion, dynamicsObj) => {
  const entry = trackingTemplates[idTrackingDescripcion];

  if (!entry) {
    return `-`;
  }

  // Si la entrada es un string plano (por compatibilidad), simplemente se retorna
  const template = typeof entry === "string" ? entry : entry.primary;
  const fallback = typeof entry === "object" && entry.fallback ? entry.fallback : null;

  // Si no hay dynamics, y se espera que haya, usamos fallback si existe
  if (!dynamicsObj || typeof dynamicsObj !== "object") {
    try {
      if (typeof dynamicsObj === "string") {
        dynamicsObj = JSON.parse(dynamicsObj);
      } else {
        dynamicsObj = {};
      }
    } catch {
      return fallback ?? template;
    }
  }

  // Detectar si se usan llaves en el template (e.g. {email_old}) y no se proporcionaron valores
  const hasKeys = /\{(\w+)\}/.test(template);
  const shouldFallback = hasKeys && Object.keys(dynamicsObj).length === 0;

  if (shouldFallback && fallback) {
    return fallback;
  }

  return template.replace(/\{(\w+)\}/g, (_, key) => {
    return dynamicsObj[key] ?? `{${key}}`;
  });
};

    useEffect(() => {
        const abortController = new AbortController();
        setIsLoading(true);
        setStatusBoxitTracking('');

        const getJSON = async (endpoint) => {
            const response = await fetch(`${getClientDomain(2)}${endpoint}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'authorization': `Bearer ${getToken()}`
                },
                signal: abortController.signal
            });

            return await response.json();
        };

        const getTrackingPaquete = async () => {
            try {
                // el tracking sale de ServTracking y las excepciones de los movimientos
                // (ServPlataformasMov.Exceptions): dos origenes distintos que se piden en paralelo y
                // se mezclan aca por fecha. Si el paquete no tiene excepciones el api responde 200
                // con lista vacia y la tabla queda igual que antes.
                const [result, exceptionsResult] = await Promise.all([
                    getJSON(`/dashboard/packages/tracking/boxit/${idPaquete}`),
                    getJSON(`/dashboard/packages/tracking/exceptions/${idPaquete}`)
                        // un fallo al traer las excepciones no debe tumbar el tracking
                        .catch((err) => { console.log(err); return null; })
                ]);

                const trackingRows = Array.isArray(result?.payload)
                    // orden explicito por IdTracking: la consulta de la vista no lleva ORDER BY, asi
                    // que el orden que devuelve SQL no esta garantizado
                    ? [...result.payload].sort((a, b) => Number(a.IdTracking) - Number(b.IdTracking)).map((row) => ({
                        // se formatea desde FechaMov —el datetime real de la misma fila— porque es
                        // el que viene en 24h; Fecha_Movimiento (12h) queda de respaldo si no viniera
                        date: getDateParts(row.FechaMov) ? formatDate(row.FechaMov) : row.Fecha_Movimiento,
                        sortKey: getSortKey(row.FechaMov),
                        status: row?.IdTrackingDescripcion
                            ? renderTrackingMessage(row.IdTrackingDescripcion, row.DynamicsObj ?? {})
                            : row.Tracking
                    }))
                    : [];

                const exceptionRows = Array.isArray(exceptionsResult?.payload)
                    ? exceptionsResult.payload.map((row) => ({
                        date: formatDate(row.date),
                        sortKey: getSortKey(row.date),
                        status: getExceptionName(row),
                        isException: true,
                        // las que no son visibles al cliente se marcan: son las que le interesan a soporte
                        isInternal: !row.customerFacing
                    }))
                    : [];

                const mappedRows = mergeTrackingWithExceptions(trackingRows, exceptionRows)
                    .map((row, index) => ({ ...row, id: index }));

                if (mappedRows.length > 0) {
                    setBoxitTrackingPaquete(mappedRows);
                    setStatusBoxitTracking('');
                } else {
                    setBoxitTrackingPaquete([]);
                    setStatusBoxitTracking(translate("dashboard.packages.details.tableStatusBoxit.noMoved"));
                }
            } catch (err) {
                console.log(err);
                setBoxitTrackingPaquete([]);
                setStatusBoxitTracking(translate("dashboard.packages.details.tableStatusBoxit.noMoved"));
            } finally {
                // Only the live request clears the flag: under StrictMode the effect
                // runs, aborts and runs again, and the discarded one used to switch
                // the indicator off for its replacement.
                if (!abortController.signal.aborted) setIsLoading(false);
            }
        };

        getTrackingPaquete();
        return () => abortController.abort();
    }, [idPaquete, updateComponent]);

    const columns = [
        {
            key: 'date',
            label: translate("global.date"),
            sortable: false,
            width: '30%',
        },
        {
            key: 'status',
            label: translate("global.status"),
            sortable: false,
            width: '70%',
            render: (row) => (
                <span style={{ display: 'block', textAlign: 'left', width: '100%' }}>
                    {row.isException ? (
                        <>
                            <MdWarningAmber size={14} style={{ color: '#d97706', marginRight: 4, verticalAlign: 'text-bottom' }} />
                            <span style={{ color: '#b45309', fontWeight: 500 }}>
                                {translate("dashboard.packages.details.tableStatusBoxit.exception")}: {row.status}
                            </span>
                            {row.isInternal && (
                                <span
                                    title={translate("dashboard.packages.details.tableStatusBoxit.exceptionInternalHelp")}
                                    style={{
                                        marginLeft: 6,
                                        padding: '1px 6px',
                                        borderRadius: 10,
                                        fontSize: 10,
                                        textTransform: 'uppercase',
                                        border: '1px solid #d1d5db',
                                        color: '#6b7280'
                                    }}
                                >
                                    {translate("dashboard.packages.details.tableStatusBoxit.exceptionInternal")}
                                </span>
                            )}
                        </>
                    ) : row.status}
                </span>
            ),
        },
    ];

    return (
        <Table
            columns={columns}
            data={boxitTrackingPaquete || []}
            loading={isLoading}
            emptyMessage={statusBoxitTracking || translate("dashboard.packages.details.tableStatusBoxit.noMoved")}
            hoverable
            striped
        />
    );
}

export default TableEstatusBoxit;